/**
 * OcrolusBooksTable — the "browse & assign" ops surface, inline on the Ocrolus
 * card stack (it used to be a modal).
 *
 * The Ocrolus account is SHARED between portals, and plenty of books were
 * created outside the LOS under ad-hoc names before this integration existed.
 * This lists every book on the account (GET /files/ocrolus_books), annotated
 * with the loan each maps to, so an orphan can be attached to this loan
 * (POST /files/assign_ocrolus_book/{dealId}).
 *
 * Transition tooling — not a permanent feature. Once books are always created
 * in the LOS this table stops being needed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import LinkRounded from "@mui/icons-material/LinkRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import { useSnackbar } from "notistack";

import AgGridTableWrapper from "../../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import SearchInput from "../../../../../components/inputs/SearchInput";
import FilterTabSwitcher from "../../../../../components/navigation/FilterTabSwitcher";
import {
  ActionCell,
  actionColumn,
} from "../../../../admin/shared/adminTableActions";
import { useLoanFilesHook } from "../../../../../hooks/useLoanFilesHook";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import colors, { status as statusColors } from "../../../../../theme/colors";
import {
  OCROLUS_COL_DEF,
  OCROLUS_GRID_ROW_H,
  OCROLUS_GRID_SX,
  OcrolusCard,
} from "./ocrolusShared";

// `portalHint` is the backend's naming-convention guess at which portal a book
// belongs to. It is a HINT, not authority — the default scope is unfiltered on
// purpose so ops can see everything while book ownership gets sorted out.
const HINT_STYLE = {
  ev: { label: "EV", bg: colors.tint, fg: colors.tintFg },
  lq: { label: "LQ", bg: statusColors.info.bg, fg: statusColors.info.fg },
  unknown: {
    label: "Unknown",
    bg: statusColors.neutral.bg,
    fg: statusColors.neutral.fg,
  },
};

// A mapped loan lives in one of the two pipelines; `pipelineCollection` (the
// vendor-order pattern) says which, so the link lands on the right drilldown.
const DRILLDOWN_BASE = {
  loan_acquisitions: "/acquisition",
  underwriting_pipeline: "/loan",
};

// Ocrolus book lifecycle, as the vendor reports it.
const CLASS_STYLE = {
  COMPLETE: statusColors.success,
  PROCESSING: statusColors.info,
  ACTIVE: statusColors.info,
};

const BookNameCell = ({ data }) => (
  <Flx column g={0.15} sx={{ height: "100%", justifyContent: "center", py: 0.5 }}>
    <Txt
      sx={{
        fontSize: 13,
        fontWeight: 500,
        color: colors.ink,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {data?.name || "Untitled book"}
    </Txt>
    <Txt sx={{ fontSize: 10.5, fontFamily: "monospace", color: colors.slate400 }}>
      {(data?.book_uuid || "").slice(0, 8) || "—"}
    </Txt>
  </Flx>
);

// A book either resolves to a loan in our DB or it doesn't. "Unassigned" is the
// interesting case — those are the assign candidates.
const MappedLoanCell = ({ data, onOpenLoan }) => {
  const mapped = data?.mappedLoan;
  if (!mapped) {
    return (
      <Flx ac sx={{ height: "100%" }}>
        <Chip
          size="small"
          label="Unassigned"
          sx={{
            height: 20,
            fontSize: 11,
            bgcolor: statusColors.warning.bg,
            color: statusColors.warning.fg,
          }}
        />
      </Flx>
    );
  }
  const label = mapped.sync_loan_id || `Loan ${mapped.loanNumber ?? ""}`.trim();
  const sub = [mapped.loanName, mapped.loanStatus].filter(Boolean).join(" · ");
  return (
    <Flx column g={0.15} sx={{ height: "100%", justifyContent: "center", py: 0.5 }}>
      <Txt
        onClick={(e) => {
          e.stopPropagation();
          onOpenLoan(data);
        }}
        sx={{
          fontSize: 12.5,
          fontWeight: 600,
          color: colors.action,
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {label}
      </Txt>
      {sub && (
        <Txt
          sx={{
            fontSize: 11,
            color: colors.slate500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {sub}
        </Txt>
      )}
    </Flx>
  );
};

const OcrolusBooksTable = ({ dealId, onAssigned }) => {
  const { getOcrolusBooks, assignOcrolusBook } = useLoanFilesHook();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ count: 0, unassigned: 0, hidden: 0 });
  const [loading, setLoading] = useState(false);
  const [assigningId, setAssigningId] = useState(null);
  const [scope, setScope] = useState("all");
  const [quickFilter, setQuickFilter] = useState("");
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  // The book the backend refused to steal from another loan (409) — assigning
  // it needs an explicit confirm, which then retries with force.
  const [reassign, setReassign] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getOcrolusBooks({
      scope,
      onSuccessFn: (data) => {
        setRows(Array.isArray(data?.books) ? data.books : []);
        setCounts({
          count: data?.count || 0,
          unassigned: data?.unassigned || 0,
          hidden: data?.hiddenCount || 0,
        });
        setLoading(false);
      },
      onFailFn: () => {
        setRows([]);
        setCounts({ count: 0, unassigned: 0, hidden: 0 });
        setLoading(false);
      },
    });
    // getOcrolusBooks is re-created each render (the hook isn't memoized).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(
    () => (unassignedOnly ? rows.filter((r) => !r?.mappedDealId) : rows),
    [rows, unassignedOnly],
  );

  const hasBookClass = useMemo(() => rows.some((r) => r?.book_class), [rows]);

  const handleOpenLoan = useCallback(
    (row) => {
      const id = row?.mappedDealId;
      if (!id) return;
      const base =
        DRILLDOWN_BASE[row?.mappedLoan?.pipelineCollection] || "/loan";
      navigate(`${base}/${id}`);
    },
    [navigate],
  );

  const handleAssign = useCallback(
    (bookUuid, { force = false } = {}) => {
      if (!bookUuid) return;
      setAssigningId(bookUuid);
      assignOcrolusBook({
        dealId,
        bookUuid,
        force,
        onSuccessFn: (res) => {
          setAssigningId(null);
          setReassign(null);
          // The Ocrolus-side rename/xid re-anchor is best-effort — the LOS
          // mapping stands either way, so this is a warning, not a failure.
          if (res && res.renamed === false) {
            enqueueSnackbar(
              "Book assigned, but Ocrolus would not rename it. Future webhooks may not resolve to this loan until the book is renamed in the Ocrolus dashboard.",
              {
                variant: "warning",
                autoHideDuration: 6000,
                anchorOrigin: { vertical: "bottom", horizontal: "right" },
              },
            );
          }
          load();
          onAssigned?.();
        },
        onFailFn: (err) => {
          setAssigningId(null);
          // 409 = the book already backs another loan. The generic toast is
          // suppressed for that status so we can offer the forced reassign.
          if (err?.response?.status === 409) {
            const row = rows.find((r) => r.book_uuid === bookUuid);
            setReassign({
              bookUuid,
              bookName: row?.name || bookUuid,
              currentLoan: row?.mappedLoan || null,
            });
          } else {
            setReassign(null);
          }
        },
      });
    },
    // assignOcrolusBook is re-created every render by useLoanFilesHook (it
    // isn't memoized), so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealId, rows, onAssigned, load, enqueueSnackbar],
  );

  const columnDefs = useMemo(
    () => [
      {
        colId: "book",
        headerName: "Book",
        field: "name",
        flex: 2,
        minWidth: 220,
        tooltipValueGetter: (p) => p.value,
        cellRenderer: BookNameCell,
      },
      {
        field: "created",
        headerName: "Created",
        width: 120,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%" }}>
            <Txt sx={{ fontSize: 12.5, color: colors.slate500 }}>
              {p.value ? formatDateTime(p.value, "MMM d") : "—"}
            </Txt>
          </Flx>
        ),
      },
      // Ocrolus' own lifecycle for the book. The list endpoint doesn't always
      // carry it (`book_class` is absent on plenty of legacy books), so the
      // column only appears when at least one row actually has a value —
      // otherwise it's a column of em-dashes.
      ...(hasBookClass
        ? [
            {
              field: "book_class",
              headerName: "Status",
              width: 130,
              cellRenderer: (p) => {
                const s = CLASS_STYLE[p.value];
                const label = p.value
                  ? `${p.value.charAt(0)}${p.value.slice(1).toLowerCase()}`
                  : "—";
                return (
                  <Flx ac sx={{ height: "100%" }}>
                    {s ? (
                      <Chip
                        size="small"
                        label={label}
                        sx={{
                          height: 20,
                          fontSize: 11,
                          bgcolor: s.bg,
                          color: s.fg,
                        }}
                      />
                    ) : (
                      <Txt sx={{ fontSize: 12.5, color: colors.slate500 }}>
                        {label}
                      </Txt>
                    )}
                  </Flx>
                );
              },
            },
          ]
        : []),
      {
        colId: "mappedLoan",
        headerName: "Mapped loan",
        flex: 1.6,
        minWidth: 190,
        // Sort on something stable rather than the rendered cell.
        valueGetter: (p) =>
          p.data?.mappedLoan?.sync_loan_id ||
          p.data?.mappedLoan?.loanNumber ||
          "",
        cellRenderer: (p) => (
          <MappedLoanCell data={p.data} onOpenLoan={handleOpenLoan} />
        ),
      },
      {
        field: "portalHint",
        headerName: "Portal",
        width: 90,
        cellRenderer: (p) => {
          const s = HINT_STYLE[p.value] || HINT_STYLE.unknown;
          return (
            <Flx ac jc sx={{ height: "100%" }}>
              <Chip
                size="small"
                label={s.label}
                sx={{ height: 20, fontSize: 11, bgcolor: s.bg, color: s.fg }}
              />
            </Flx>
          );
        },
      },
      actionColumn({
        colId: "assign",
        width: 92,
        cellRenderer: (p) => {
          const uuid = p.data?.book_uuid;
          const busy = assigningId === uuid;
          const isThisLoan = p.data?.mappedDealId === dealId;
          return (
            <ActionCell>
              <Tooltip
                title={
                  isThisLoan
                    ? "Already assigned to this loan"
                    : "Assign this book to the current loan"
                }
              >
                {/* span keeps the Tooltip working while the button is disabled */}
                <span>
                  <Button
                    variant="text"
                    size="small"
                    disabled={busy || !uuid || isThisLoan}
                    onClick={() => handleAssign(uuid)}
                    startIcon={
                      busy ? null : <LinkRounded sx={{ fontSize: 15 }} />
                    }
                    sx={{ fontSize: 12 }}
                  >
                    {busy ? <CircularProgress size={14} /> : "Assign"}
                  </Button>
                </span>
              </Tooltip>
            </ActionCell>
          );
        },
      }),
    ],
    [assigningId, dealId, handleAssign, handleOpenLoan, hasBookClass],
  );

  return (
    <OcrolusCard
      title="All Ocrolus books"
      subtitle="Assign books created outside the portal to their loans"
      endContent={
        <>
          <Txt sx={{ fontSize: 12, color: colors.slate500 }}>
            {`${counts.count} book${counts.count === 1 ? "" : "s"} · ${
              counts.unassigned
            } unassigned`}
          </Txt>
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={load} disabled={loading}>
                {loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshRounded sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </>
      }
    >
      {/* One row of filters, all vertically centred on each other. The Scope
          switcher used to sit under a stacked "Scope" label, which pushed it
          off the baseline of everything beside it and left the checkbox label
          floating — the label is inline now and the row centres. */}
      <Flx ac g={2} wrap sx={{ px: 2, py: 1.5 }}>
        {/* Lone quick-filter box — the one control that may keep a floating
            label per the form-input convention. */}
        <SearchInput
          value={quickFilter}
          onChange={setQuickFilter}
          size="small"
          placeholder="Search books or loans..."
          sx={{ maxWidth: 280 }}
        />
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Checkbox
              size="small"
              checked={unassignedOnly}
              onChange={(e) => setUnassignedOnly(e.target.checked)}
              sx={{ py: 0.25 }}
            />
          }
          label={
            <Txt sx={{ fontSize: 12, color: colors.slate600 }}>
              Unassigned only
            </Txt>
          }
        />
        <Flx ac g={1} sx={{ ml: "auto" }}>
          <Txt sx={{ fontSize: 12, color: colors.slate500 }}>Scope</Txt>
          <FilterTabSwitcher
            fontSize={11}
            value={scope}
            onChange={setScope}
            tabs={[
              { label: "All books", value: "all" },
              { label: "This portal", value: "portal" },
            ]}
          />
        </Flx>
      </Flx>

      {scope === "portal" && counts.hidden > 0 && (
        <Txt sx={{ fontSize: 12, color: colors.slate500, px: 2, pb: 1 }}>
          {counts.hidden} book{counts.hidden === 1 ? "" : "s"} belong to the
          other portal.
        </Txt>
      )}

      <AgGridTableWrapper
        compactTable
        noBorder
        height={420}
        sx={OCROLUS_GRID_SX}
      >
        <AgGridReact
          rowData={visibleRows}
          columnDefs={columnDefs}
          quickFilterText={quickFilter}
          getRowId={(p) => p.data?.book_uuid}
          rowHeight={OCROLUS_GRID_ROW_H}
          pagination
          paginationPageSize={25}
          tooltipShowDelay={300}
          defaultColDef={OCROLUS_COL_DEF}
          suppressCellFocus
          overlayNoRowsTemplate={
            loading ? "Loading books…" : "No books on the account."
          }
        />
      </AgGridTableWrapper>

      {/* A book backs exactly ONE loan. The backend returns 409 rather than
          silently stealing it from another deal — confirm, then retry with
          force. */}
      <Dialog
        open={!!reassign}
        onClose={() => setReassign(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Txt sx={{ fontSize: 14, fontWeight: 600, color: colors.ink }}>
            Reassign this book?
          </Txt>
        </DialogTitle>
        <DialogContent>
          <Txt sx={{ fontSize: 12.5, color: colors.slate600 }} component="div">
            <b>{reassign?.bookName}</b> is already assigned to{" "}
            {reassign?.currentLoan?.sync_loan_id ||
              (reassign?.currentLoan?.loanNumber
                ? `loan ${reassign.currentLoan.loanNumber}`
                : "another loan")}
            . Reassigning moves the book — and the bank analysis it produces —
            to this loan, and renames it in Ocrolus to this loan's number.
          </Txt>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setReassign(null)}>
            Cancel
          </Button>
          <Button
            disabled={!!assigningId}
            onClick={() => handleAssign(reassign?.bookUuid, { force: true })}
          >
            Reassign to this loan
          </Button>
        </DialogActions>
      </Dialog>
    </OcrolusCard>
  );
};

export default OcrolusBooksTable;
